<template>
  <div :key="lang">
    <section class="mt-3" v-if="!user && props.errorType">
      <div class="container-lg px-4 py-5 my-5 text-center">
        <h1 class="display-5">{{ props.errorType }}</h1>
        <div class="col-lg-6 mx-auto">
          <p class="lead mb-4">{{ props.errorMessage }}</p>
        </div>
      </div>
    </section>

    <section id="intro" class="bg-light" v-if="!user && !props.errorType">
      <div class="container-lg">
        <div class="row flex-lg-row-reverse align-items-center g-5 py-2">
          <div class="col-10 col-sm-8 col-lg-6">
            <div class="tutorial container text-center my-5 ratio ratio-4x3">
              <iframe src="https://www.youtube.com/embed/ly6A2b_Rtug?si=eLetpdgeNXSProject" allowfullscreen></iframe>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="px-4 py-5 my-5 text-center" v-if="translated">
              <h1 class="display-4">{{ translated.newUserTitle }}</h1>
              <div class="mx-auto">
                <p class="lead mb-4">{{ translated.newUserMessage }}</p>
                <div class="d-grid gap-2 d-sm-flex justify-content-sm-center">
                  <router-link :to="{ name: 'Register' }" class="btn btn-primary btn-lg px-4 gap-3">{{ translated.button?.register }}</router-link>
                  <button @click="handleLogin" class="btn btn-lg px-4 gap-3 btn-outline-primary">{{ translated.button?.login }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mt-5" v-if="!user && !props.errorType">
      <div class="container-lg" v-if="translated">
        <h3 class="display-6">{{ translated.supportTitle }}</h3>
        <p>{{ translated.supportText }} <a href="mailto:help@demogfe.com">help@demogfe.com</a>.</p>
      </div> 
      <div class="toast-container position-absolute bottom-0 end-0 me-3 mb-2">
        <div ref="el" class="toast" role="alert" aria-live="assertive" aria-atomic="true" data-bs-autohide="false">
          <div class="toast-body">This site uses cookies from Google to deliver its services and to analyze traffic. Information about your use of this site is shared with Google. By using this site, you agree to its use of cookies.
            <div class="mt-2 pt-2 border-top">
              <button type="button" class="btn btn-primary btn-sm me-2" data-bs-dismiss="toast">Got it</button>
              <a href="https://policies.google.com/technologies/cookies" target="_blank" class="btn btn-secondary btn-sm">Learn more</a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="intro" class="mt-3" v-if="user">
      <div class="container-lg">
        <div class="row flex-lg-row-reverse align-items-center g-5 py-2">
          <div class="col-10 col-sm-8 col-lg-6">
            <div class="tutorial container text-center my-5 ratio ratio-4x3">
              <iframe src="https://www.youtube.com/embed/gFDuMNnmOx4?si=EhGvSzzZgdK5U_sf" allowfullscreen></iframe>
            </div>
          </div>
          <div class="col-lg-6" v-if="translated">
            <h1 class="display-6">{{ translated.title }}</h1>
            <p class="lead mb-4">{{ translated.welcome }}</p>
            <div class="d-grid gap-2 d-sm-flex">
              <a href="#createDemo" class="btn px-4 gap-3 btn-primary">{{ translated.button?.createDemo }}</a>
              <a href="https://docs.google.com/presentation/d/1BBCGlsCqtLqUN4uoJtTnznUOw8vksflvWbvwMAF1i1o/edit?slide=id.g35008366afa_0_0#slide=id.g35008366afa_0_0" target="_blank" class="btn px-4 gap-3 btn-outline-primary">Demo Toolkit</a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="user && translated && translated.features">
      <div class="container-lg mt-4">
        <div class="row">
          <div class="col-3 text-center">
            <h2><i class="bi bi-google" id="icon"></i></h2>
            <h4 class="display-7">{{ translated.features.title1 }}</h4>
            <p class="text-secondary">{{ translated.features.text1 }}</p>
          </div>
          <div class="col-3 text-center">
            <h2><i class="bi bi-person-check-fill" id="icon"></i></h2>
            <h4 class="display-7">{{ translated.features.title2 }}</h4>
            <p class="text-secondary">{{ translated.features.text2 }}</p>
          </div>
          <div class="col-3 text-center">
            <h2><i class="bi bi-puzzle-fill" id="icon"></i></h2>
            <h4 class="display-7">{{ translated.features.title3 }}</h4>
            <p class="text-secondary">{{ translated.features.text3 }}</p>
          </div>
          <div class="col-3 text-center">
            <h2><i class="bi bi-shield-lock" id="icon"></i></h2>
            <h4 class="display-7">{{ translated.features.title4 }}</h4>
            <p class="text-secondary">{{ translated.features.text4 }}</p>
          </div>
        </div>
      </div>  
    </section>

    <section id="dashboard" class="mt-4 pt-4 border-top" v-if="user">
      <div class="container-lg">
        <div class="mb-4">
          <h3 class="fw-bold">Your Presenter Dashboard</h3>
          <p class="text-secondary">Manage your short-lived features and track active environments from one place.</p>
        </div>

        <div class="row g-4 mb-5">
          <div class="col-md-6">
            <div class="card h-100 shadow-sm border-0" :class="isGeminiActive ? 'border-start border-success border-4 bg-success bg-opacity-10' : 'border-start border-primary border-4 bg-light'">
              <div class="card-body d-flex flex-column justify-content-between p-4">
                <div>
                  <div class="d-flex align-items-center mb-2">
                    <h4 class="fw-bold m-0 me-2"><i class="bi bi-stars text-primary"></i> Gemini Workspace Access</h4>
                    <span v-if="isGeminiActive" class="badge bg-success">Active</span>
                    <span v-else class="badge bg-secondary">Disabled</span>
                  </div>
                  <p class="small text-muted mb-4">
                    Temporary Google AI Pro license upgrades expire after 24 hours. Activate your temporary license to explore Gemini features on Google Workspace.
                  </p>
                </div>
                
                <div v-if="isGeminiActive && userInfo && userInfo.document && userInfo.document.duetAITime">
                  <div class="alert alert-success d-flex align-items-center mb-0 py-2">
                    <i class="bi bi-clock-history me-2"></i>
                    <span class="me-1">Time Remaining:</span>
                    <strong><CountdownTimer :startingMinutes="timeDifferenceInMinutes(userInfo.document.duetAITime)" /></strong>
                  </div>
                </div>
                <div v-else>
                  <button @click="enableGemini" class="btn btn-primary px-4 rounded-pill" :disabled="processingGemini">
                    <span v-if="processingGemini" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <i v-else class="bi bi-lightning-charge-fill me-1"></i> Enable Temporary Gemini Access
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="card h-100 shadow-sm border-0 bg-light">
              <div class="card-body p-4 d-flex flex-column justify-content-between">
                <div>
                  <h4 class="fw-bold mb-3"><i class="bi bi-clock-history text-secondary"></i> Your Recent Sessions</h4>
                  <div v-if="recentSessions && recentSessions.length > 0" class="table-responsive">
                    <table class="table table-sm table-hover bg-white rounded border mb-0" style="font-size: 0.9rem;">
                      <thead>
                        <tr class="table-light">
                          <th>Session Name</th>
                          <th>Demo Context</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="session in recentSessions" :key="session.id">
                          <td>
                            <router-link :to="{ name: 'SessionDetails', params: { id: session.id }}" class="fw-bold text-decoration-none">
                              {{ session.sessionName }} <i class="bi bi-box-arrow-in-right"></i>
                            </router-link>
                          </td>
                          <td class="text-truncate text-muted" style="max-width: 180px;">{{ session.demoName }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-center py-4 text-muted">
                    <p class="small mb-0">You haven't spun up any custom demo environments yet.</p>
                  </div>
                </div>
                <div class="text-end mt-2">
                  <router-link :to="{ name: 'ManageDemos' }" class="small text-decoration-none fw-bold">View all sessions &raquo;</router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="createDemo" class="mt-5 bg-light" v-if="user">
      <div class="container-lg">
        <div class="row">
          <div class="col-9">
            <h2 class="display-5" v-if="translated">{{ translated.createDemoTitle }}</h2>
            <p v-if="translated">{{ translated.createDemoText }}</p>
          </div>
          <div class="col-3">
            <div class="row d-flex align-items-center">
              <div class="col-4">
                <p class="text-end mb-0 small">Sort by</p>
              </div>
              <div class="col-8">
                <select class="form-select" id="sort" @change="sortBy(sortType)" v-model="sortType">
                  <option value="mostPopular">Most popular</option>
                  <option value="createdAt">Recently added</option>
                  <option value="AZ">A-Z</option>
                  <option value="ZA">Z-A</option>
                </select>
              </div>
            </div>
            
            <div class="row mt-2 d-flex align-items-center">
              <div class="col-4">
                <p class="text-end mb-0 small">View as</p>
              </div>
              <div class="col-8">
                <div class="btn-group w-100 shadow-sm" role="group">
                  <button @click="viewType = 'list'" type="button" class="btn btn-sm btn-white border border-end-0 py-1" :class="{'btn-primary text-white': viewType === 'list'}">
                    <i class="bi bi-list-task"></i> List
                  </button>
                  <button @click="viewType = 'grid'" type="button" class="btn btn-sm btn-white border py-1" :class="{'btn-primary text-white': viewType === 'grid'}">
                    <i class="bi bi-grid-3x3-gap"></i> Grid
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      
        <div class="row mt-4">
          <div class="col-md-3">
            <div class="position-sticky bg-white p-4 rounded border" style="top: 4rem;">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h4 v-if="translated"><i class="bi bi-filter"></i> {{ translated.filterTitle }}</h4>
                <button v-if="tagsArray && tagsArray.length > 0" @click="clearAllFilters" type="button" class="btn btn-sm btn-outline-danger py-0 px-2 rounded-pill" style="font-size: 0.8rem;">
                  Clear All
                </button>
              </div>
              <form>
                <div v-for="form in forms" :key="form.title">
                  <div class="d-grid gap-2 border-top border-bottom p-1 mb-2">
                    <a class="link-dark text-decoration-none fs-5" type="button" data-bs-toggle="collapse" :data-bs-target="'#'+form.title.replace(/\s+/g, '-').toLowerCase()" role="button" aria-expanded="true">
                      <i class="bi bi-chevron-down"></i> {{ form.title }}
                    </a>
                  </div>
                  <div class="collapse show" :id="form.title.replace(/\s+/g, '-').toLowerCase()">
                    <div v-for="field in form.fields" :key="field.value" class="form-check">
                      <check-box :fieldId="field.name" v-model:tags="tagsArray" :value="field.value" />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div class="col-md-9">
            <div v-if="viewType === 'list'" class="d-flex flex-column gap-3 mb-5">
              <div v-for="demo in demosWithTags" :key="demo.id" class="card border-0 shadow-sm p-3 d-flex flex-column flex-md-row align-items-md-center gap-4">
                
                <div class="d-flex flex-shrink-0 align-items-center justify-content-center bg-light rounded border" style="width: 60px; height: 60px;">
                  <i v-if="demo.apps && demo.apps.includes('gemini')" class="bi bi-stars text-primary fs-3"></i>
                  <i v-else-if="demo.apps && demo.apps.includes('admin')" class="bi bi-shield-lock-fill text-success fs-3"></i>
                  <i v-else class="bi bi-google text-warning fs-3"></i>
                </div>
                
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <h5 class="fw-bold m-0 text-dark">
                      {{ getLocalizedText(demo.title) }}
                    </h5>
                    <span class="badge bg-secondary px-2" style="font-size: 0.65rem;" v-if="demo.visibility">
                      {{ demo.visibility }}
                    </span>
                  </div>
                  
                  <p class="text-muted small m-0 pe-xl-5">
                    {{ getLocalizedText(demo.description) }}
                  </p>
                </div>

                <div class="flex-shrink-0 text-md-end mt-2 mt-md-0">
                  <router-link :to="{ name: 'DemoDetails', params: { id: demo.id}}" class="btn btn-primary px-4 rounded-pill shadow-sm">
                    Choose Template <i class="bi bi-arrow-right ms-1"></i>
                  </router-link>
                </div>

              </div>
            </div>

            <div v-else class="row my-5 align-items-stretch justify-content-left">
              <div class="d-flex col-auto" v-for="demo in demosWithTags" :key="demo.id">
                <div class="card border-1 mb-4 shadow-sm" style="width: 18rem;">
                  <div class="card-body text-center py-4 d-flex flex-column">
                    <h6>
                      <span class="badge bg-secondary text-capitalize" v-if="demo.visibility">{{ demo.visibility }}</span>
                    </h6>
                    <h4 class="card-title">
                      {{ getLocalizedText(demo.title) }}
                    </h4>
                    
                    <p class="card-text mx-5 text-muted d-none d-sm-block">
                      {{ getLocalizedText(demo.description).length > 100 
                         ? getLocalizedText(demo.description).substring(0, Math.min(100, getLocalizedText(demo.description).substring(0, 100).lastIndexOf(' '))) + '...' 
                         : getLocalizedText(demo.description) 
                      }}
                    </p>
                  
                    <router-link :to="{ name: 'DemoDetails', params: { id: demo.id}}" class="btn btn-outline-primary border-0 stretched-link mt-auto" v-if="translated">
                      {{ translated.button?.chooseDemo }} <i class="bi bi-arrow-right"></i>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div> 
    </section>

    <section class="mt-5" v-if="user">
      <div class="container-lg">
        <h2 class="display-5" v-if="translated">{{ translated.additionalResources }}</h2>
        <div class="row">
          <div class="col-md-6">
            <h4>Slide decks</h4>
            <ul class="icon-list">
              <li><a href="https://docs.google.com/presentation/d/1nJAZYHrAe-K0OOqZ3HA1-YrY6aNO5yOIV5MosOkaIOU/edit5" rel="noopener" target="_blank">What’s new in Google for Education</a></li>
              <li><a href="https://docs.google.com/presentation/d/1ZxQlEng6Hw-1fPfiR1LuY0NRlqpgYZIUhxTLcqfSmBk/edit#slide=id.gbdc2b235bf_1_2840" rel="noopener" target="_blank">40+ ways to use Google Workspace for Education paid editions</a></li>
              <li><a href="https://docs.google.com/presentation/d/1BBCGlsCqtLqUN4uoJtTnznUOw8vksflvWbvwMAF1i1o/edit?slide=id.g35008366afa_0_0#slide=id.g35008366afa_0_0" rel="noopener" target="_blank">Google for Education Demo Toolkit</a></li>
            </ul>
          </div>
          <div class="col-md-6">
            <h4>Videos</h4>
            <ul class="icon-list">
              <li><a href="https://www.youtube.com/playlist?list=PLP7Bvyb3ap44AB9oQclvFrXTFsoZJyJs6">Google Workspace for Education Demo Series</a></li>
              <li><a href="https://edu.google.com/intl/ALL_uk/get-started/paid-workspace-admin-support/">Get started with the paid editions of Google Workspace for Education</a></li>
            </ul>
          </div>
        </div>
      </div>  
    </section>
  </div>  
</template>

<script>
import { computed, ref } from 'vue'
import axios from 'axios'
// IMPORTANT FIX: Import BOTH getCollection scripts 
// to separate the global state of the Demos vs the Sessions!
import getCollection2 from '../composables/getCollection2' 
import getCollection from '../composables/getCollection' 

import Checkbox from "../components/CheckBox.vue"
import getUser from '../composables/getUser'
import translate from '../composables/translate'
import useLogin from '../composables/useLogin'
import { useStore } from 'vuex'
import { Toast } from 'bootstrap'
import CountdownTimer from '../components/CountdownTimer.vue'

export default {
  name: 'Home',
  props: {
    errorType: String,
    errorMessage: String
  },
  components: {
    "check-box": Checkbox,
    CountdownTimer
  },
  setup(props) {
    const tagsArray = ref([]) 
    
    const { user, userInfo } = getUser()
    const store = useStore()
    const lang = computed(() => store.state.lang)
    const { getLanguage, translated, forms } = translate()
    getLanguage('home', lang)

    const viewType = ref('list') 
    const processingGemini = ref(false)

    const { login } = useLogin()
    const handleLogin = async () => {
      await login()
    }
    
    const sortType = ref('mostPopular')
    const sorted = ref(['count', 'desc'])
    
    const sortBy = ((value) => {
      if (value === 'mostPopular'){
        sorted.value = ['count', 'desc']
        getCollection2('demos', '', sorted.value)
      }
      if (value === 'createdAt'){
        sorted.value = ['createdAt', 'desc']
        getCollection2('demos', '', sorted.value)
      }
      if (value === 'AZ') {
        sorted.value = ['title', 'asc']
        getCollection2('demos', '', sorted.value)
      }
      if (value === 'ZA') {
        sorted.value = ['title', 'desc']
        getCollection2('demos', '', sorted.value)
      }
    })
    
    // FETCH DEMOS using getCollection2 (Supports external ref sorting)
    const { documents: demos } = getCollection2('demos', '', sorted.value)
    
    // FETCH SESSIONS using getCollection (Safely scoped ref, prevents overwrite)
    const { documents: sessions } = getCollection('trainings', user.value ? ['createdBy', '==', user.value.email] : null, ['createdAt', 'desc'])

    const recentSessions = computed(() => {
      if (!sessions.value) return []
      return sessions.value.slice(0, 3)
    })

    const isGeminiActive = computed(() => {
      if (!userInfo.value || !userInfo.value.document || !userInfo.value.document.duetAI || !userInfo.value.document.duetAITime) return false
      return timeDifferenceInMinutes(userInfo.value.document.duetAITime) >= 0
    })

    const timeDifferenceInMinutes = (duetAITime) => {
      if (!duetAITime) return 0
      const duetAIDate = duetAITime.toDate()
      duetAIDate.setHours(duetAIDate.getHours() + 8) 
      const now = new Date()
      const diffInMs = duetAIDate - now
      return Math.floor(diffInMs / (1000 * 60))
    }

    const enableGemini = async () => {
      if (!user.value || !userInfo.value || !userInfo.value.document || !userInfo.value.document.id) return
      processingGemini.value = true
      try {
        await axios.get('https://us-central1-demotool-e1a5e.cloudfunctions.net/enableDuetAI?email=' + user.value.email + '&userDoc=' + userInfo.value.document.id)
        window.location.reload()
      } catch (e) {
        console.error("Gemini activation issue", e)
      } finally {
        processingGemini.value = false
      }
    }

    const clearAllFilters = () => {
      if (tagsArray.value) {
        tagsArray.value = []
      }
    }

    const demosWithTags = computed(() => {
      if (!demos.value) return [];
      const currentTags = tagsArray.value || []; 

      return demos.value.filter(d => {
        const tagsMatch = currentTags.every(item => d.tags && d.tags.includes(item));
        
        const visibility = d.visibility || 'public'; 

        if (!user.value) return tagsMatch && visibility === 'public';
        
        const createdByUser = d.createdBy === user.value.email;
        const sharedWithEmail = visibility === 'shared' && (d.sharedWith || []).includes(user.value.email);
        
        let userDomain = '';
        if (userInfo.value && userInfo.value.document) {
           const docObj = userInfo.value.document.value || userInfo.value.document;
           userDomain = docObj.userDomain || '';
        }

        const sharedWithDomain = visibility === 'shared' && (d.sharedWithDomain || []).includes(userDomain); 
        
        const visible = visibility === 'public' || createdByUser || sharedWithEmail || sharedWithDomain;
        
        return tagsMatch && visible;
      });
    });

    const getLocalizedText = (field) => {
      if (!field) return '';
      if (typeof field === 'string') return field; 
      
      let userLang = 'en';
      try {
        if (userInfo.value && userInfo.value.document) {
          const docObj = userInfo.value.document.value || userInfo.value.document;
          if (docObj && docObj.language) {
             userLang = docObj.language;
          }
        }
      } catch (e) {
         console.warn("Failed retrieving user lang", e)
      }
      
      if (field[userLang]) return field[userLang];
      if (field['en']) return field['en'];
      
      const values = Object.values(field);
      return values.length > 0 ? values[0] : '';
    };

    return { 
      demos, 
      tagsArray, 
      demosWithTags, 
      user, 
      translated, 
      lang, 
      handleLogin, 
      forms, 
      userInfo, 
      props, 
      sortBy, 
      sortType,
      viewType,
      recentSessions,
      sessions,
      isGeminiActive,
      timeDifferenceInMinutes,
      enableGemini,
      processingGemini,
      clearAllFilters,
      getLocalizedText
    }
  },
  mounted() {
    try {
      if (this.$refs.el) {
        this.toast = new Toast(this.$refs.el);
        this.toast.show();
      }
    } catch(e) { console.log(e) }
  }
}
</script>

<style>
#icon {
  color: #0F9D58;
}
.icon-list {
  padding-left: 0;
  list-style: none;
}
.icon-list li {
  display: flex;
  align-items: flex-start;
  margin-bottom: .25rem;
}
.icon-list li::before {
  display: block;
  flex-shrink: 0;
  width: 1.5em;
  height: 1.5em;
  margin-right: .5rem;
  content: "";
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23212529' viewBox='0 0 16 16'%3E%3Cpath d='M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H4.5z'/%3E%3C/svg%3E") no-repeat center center / 100% auto;
}
.w-sm-100 {
  @media (max-width: 576px) {
    width: 100% !important;
  }
}
</style>